import { Component, OnInit } from '@angular/core';
import { AuthService } from '@services/auth.service';
import { StoreService } from '@services/store.service';
import { NotificationService } from '@services/notification.service';
import { Store } from '@models/store';
import {
  CommissionAdjustment,
  CommissionEmployee,
  CommissionHistory,
  CommissionReport,
  CommissionSale,
  CommissionService,
  CommissionSettings,
} from '@services/commission.service';

@Component({
  selector: 'app-commission',
  templateUrl: './commission.component.html',
  styleUrls: ['./commission.component.css'],
})
export class CommissionComponent implements OnInit {
  stores: Store[] = [];
  storeId = 'all';
  view = 'open';
  year = new Date().getFullYear();
  years: number[] = [];
  loading = true;
  saving = false;
  report: CommissionReport | null = null;
  settings: CommissionSettings = {
    store: null,
    groAmount: 0,
    rgAmount: 0,
    readerAmount: 0,
  };
  viewingSheet = false;
  reassignSale: CommissionSale | null = null;
  reassignEmployeeId = '';
  reassignReason = '';
  printReport: CommissionReport | null = null;
  printEmployeeId: string | null = null;
  private savedSettings = '';

  constructor(
    private commissionService: CommissionService,
    private storeService: StoreService,
    private notificationService: NotificationService,
    public auth: AuthService
  ) {}

  ngOnInit(): void {
    this.years = Array.from({ length: 6 }, (_, index) => this.year - index);
    this.stores = this.storeService.stores || [];
    this.storeService.getAllStores().subscribe((stores) => {
      this.stores = stores || [];
    });
    this.load();
  }

  get isAdmin(): boolean {
    return this.auth.currentUser?.role === 'OWNER/ADMIN';
  }

  get canOperate(): boolean {
    const role = this.auth.currentUser?.role;
    return role === 'OWNER/ADMIN' || role === 'MANAGER';
  }

  get canReassign(): boolean {
    if (this.viewingSheet || this.report?.sheet?.status === 'paid') {
      return this.isAdmin;
    }
    return this.canOperate && this.view === 'open';
  }

  get statusLine(): string {
    if (this.viewingSheet) {
      return 'Paid sheet is locked. Admin edits are audited.';
    }
    if (this.view === 'ytd') {
      return `Year to date ${this.year}.`;
    }
    if (this.view === 'year') {
      return `Full year ${this.year}.`;
    }
    return 'Current sheet is open.';
  }

  get openedLabel(): string {
    const opened = this.report?.sheet?.openedAt;
    if (!opened || this.view !== 'open' || this.viewingSheet) {
      return 'The current open sheet began when the previous sheet was marked paid. Qualifying sales populate the correct employee section automatically.';
    }
    return `This open sheet began ${this.longDate(opened)}. Qualifying sales populate the correct employee section automatically.`;
  }

  load(): void {
    this.loading = true;
    this.commissionService
      .getReport({
        store: this.storeId,
        view: this.viewingSheet ? 'open' : this.view,
        year: this.year,
        sheet: this.viewingSheet && this.report?.sheet?._id ? this.report.sheet._id : undefined,
        settingsStore: this.auth.getStore(),
      })
      .subscribe({
        next: (report) => {
          this.report = report;
          this.settings = {
            store: report.settings?.store ?? null,
            groAmount: Number(report.settings?.groAmount) || 0,
            rgAmount: Number(report.settings?.rgAmount) || 0,
            readerAmount: Number(report.settings?.readerAmount) || 0,
          };
          this.savedSettings = JSON.stringify(this.settings);
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.notificationService.show(
            'error',
            error?.error?.message || 'Unable to load the commission sheet.',
            'Close'
          );
        },
      });
  }

  onFilterChange(): void {
    this.viewingSheet = false;
    this.load();
  }

  saveSettings(): void {
    if (!this.isAdmin || this.saving) {
      return;
    }
    const payload = {
      store: this.storeId === 'all' ? Number(this.auth.getStore()) : Number(this.storeId),
      groAmount: Number(this.settings.groAmount) || 0,
      rgAmount: Number(this.settings.rgAmount) || 0,
      readerAmount: Number(this.settings.readerAmount) || 0,
    };
    if (!Number.isFinite(payload.store) || payload.store <= 0) {
      this.notificationService.show('error', 'Choose a store before saving amounts.', 'Close');
      return;
    }
    if (JSON.stringify({ ...this.settings, ...payload }) === this.savedSettings) {
      return;
    }
    this.saving = true;
    this.commissionService.saveSettings(payload).subscribe({
      next: () => {
        this.saving = false;
        this.load();
      },
      error: (error) => {
        this.saving = false;
        this.notificationService.show(
          'error',
          error?.error?.message || 'Unable to save commission amounts.',
          'Close'
        );
      },
    });
  }

  markPaid(): void {
    const total = (this.report?.employees || []).reduce((sum, employee) => sum + employee.total, 0);
    const confirmed = window.confirm(
      `Mark this sheet paid at ${this.money(total)}? It will lock, and a new blank sheet will open.`
    );
    if (!confirmed) {
      return;
    }
    this.commissionService.markPaid(this.storeId).subscribe({
      next: () => {
        this.viewingSheet = false;
        this.view = 'open';
        this.load();
      },
      error: (error) => {
        this.notificationService.show(
          'error',
          error?.error?.message || 'Unable to mark the sheet paid.',
          'Close'
        );
      },
    });
  }

  viewSheet(row: CommissionHistory): void {
    this.viewingSheet = true;
    this.loading = true;
    this.commissionService
      .getReport({
        store: this.storeId,
        view: 'open',
        year: this.year,
        sheet: row.id,
        settingsStore: this.auth.getStore(),
      })
      .subscribe({
        next: (report) => {
          this.report = { ...report, sheet: { ...(report.sheet || {}), _id: row.id, status: 'paid' } };
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.viewingSheet = false;
          this.notificationService.show(
            'error',
            error?.error?.message || 'Unable to open that sheet.',
            'Close'
          );
        },
      });
  }

  backToOpen(): void {
    this.viewingSheet = false;
    this.view = 'open';
    this.load();
  }

  openReassign(sale: CommissionSale): void {
    if (!this.canReassign) {
      return;
    }
    this.reassignSale = sale;
    this.reassignEmployeeId = this.report?.users?.[0]?.id || '';
    this.reassignReason = '';
  }

  saveReassign(): void {
    if (!this.reassignSale || !this.reassignReason.trim() || !this.reassignEmployeeId) {
      this.notificationService.show('error', 'Choose an employee and enter a reason.', 'Close');
      return;
    }
    this.commissionService
      .reassign(this.reassignSale.id, this.reassignEmployeeId, this.reassignReason.trim())
      .subscribe({
        next: () => {
          this.reassignSale = null;
          if (this.viewingSheet && this.report?.sheet?._id) {
            this.viewSheet({ id: this.report.sheet._id } as CommissionHistory);
            return;
          }
          this.load();
        },
        error: (error) => {
          this.notificationService.show(
            'error',
            error?.error?.message || 'Unable to reassign this commission.',
            'Close'
          );
        },
      });
  }

  printYtd(employeeId: string | null): void {
    this.commissionService
      .getReport({
        store: this.storeId,
        view: 'ytd',
        year: this.year,
        settingsStore: this.auth.getStore(),
      })
      .subscribe({
        next: (report) => {
          this.printReport = report;
          this.printEmployeeId = employeeId;
          setTimeout(() => this.printNow());
        },
        error: () => {
          this.notificationService.show('error', 'Unable to prepare the YTD report.', 'Close');
        },
      });
  }

  employeesOf(report: CommissionReport | null): CommissionEmployee[] {
    const employees = report?.employees || [];
    if (!this.printEmployeeId) {
      return employees;
    }
    return employees.filter((employee) => employee.id === this.printEmployeeId);
  }

  money(value: number, cents = true): string {
    const amount = Number(value) || 0;
    const fraction = cents || amount % 1 !== 0 ? 2 : 0;
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: fraction,
      maximumFractionDigits: 2,
    });
  }

  shortDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  }

  longDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  }

  paidStamp(row: CommissionHistory): string {
    const date = new Date(row.paidAt);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `Marked paid by ${row.paidByName || 'ADMIN'} on ${this.longDate(row.paidAt)} at ${time}`;
  }

  adjusted(row: CommissionHistory): boolean {
    return Math.abs((Number(row.adjustedTotal) || 0) - (Number(row.total) || 0)) >= 0.005;
  }

  adjustmentText(row: CommissionAdjustment): string {
    const who = row.reason?.byName ? ` by ${row.reason.byName}` : '';
    const why = row.reason?.reason || 'Returned or canceled';
    return `${row.patientLastName || 'STOCK'} ${row.model || ''} removed ${this.money(row.amount)}${who}. ${why}`;
  }

  private printNow(): void {
    const style = document.createElement('style');
    style.id = 'ao-commission-print';
    style.textContent = `
      @media print {
        body * { visibility: hidden; }
        app-commission, app-commission * { visibility: visible; }
        app-commission .screen, app-commission .screen * { display: none !important; visibility: hidden !important; }
        app-commission .print-sheet { display: block !important; visibility: visible !important; position: absolute; left: 0; top: 0; width: 100%; background: white; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    style.remove();
    this.printReport = null;
    this.printEmployeeId = null;
  }
}
